<template>
  <div ref="container" class="overuse-notifications">
    <button
      ref="trigger"
      type="button"
      class="overuse-bell"
      aria-label="초과사용 알림"
      aria-haspopup="dialog"
      :aria-expanded="open"
      aria-controls="overuse-notification-panel"
      @click="toggle"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.7"
        aria-hidden="true"
      >
        <path d="M6 9a6 6 0 0 1 12 0c0 7 3 7 3 9H3c0-2 3-2 3-9Z" />
        <path d="M10 21h4M12 1v2" />
      </svg>
      <span v-if="devices.length" class="overuse-badge">{{
        devices.length
      }}</span>
    </button>
    <Teleport to="body">
      <section
        v-if="open"
        id="overuse-notification-panel"
        class="overuse-popover"
        :style="popoverStyle"
        role="dialog"
        aria-labelledby="overuse-notification-title"
      >
        <header>
          <div>
            <h3 id="overuse-notification-title">초과사용 안내</h3>
            <p>24시간 이상 연속 사용 중인 기기입니다.</p>
          </div>
          <button
            type="button"
            class="overuse-close"
            aria-label="초과사용 안내 닫기"
            @click="close(true)"
          >
            ×
          </button>
        </header>
        <p v-if="error" class="overuse-refresh-error" role="status">
          {{ error }}{{ hasLoaded ? " 마지막 조회 결과를 표시합니다." : "" }}
        </p>
        <p v-if="loading" class="overuse-loading" role="status">
          알림을 확인하고 있습니다.
        </p>
        <p v-if="hasLoaded" class="overuse-notification-count">
          전체 조회 범위 · {{ devices.length }}대
        </p>
        <ul v-if="devices.length" class="overuse-notification-list">
          <li v-for="device in devices" :key="device.device_uuid">
            <button
              type="button"
              class="overuse-notification-item"
              @click="selectDevice(device)"
            >
              <strong>{{ device.school_name || "학교 미지정" }}</strong
              ><span
                >{{ device.school_region || "지역 미지정" }} ·
                {{ device.app_location || "위치 미지정" }}</span
              ><b
                >{{
                  Math.floor(statusHours(device)).toLocaleString("ko-KR")
                }}시간 연속 사용 중</b
              >
            </button>
          </li>
        </ul>
        <p
          v-else-if="hasLoaded && !loading && !error"
          class="overuse-notification-empty"
        >
          현재 초과사용 중인 기기가 없습니다.
        </p>
      </section>
    </Teleport>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { onMounted, onBeforeUnmount, ref, nextTick } from "vue";
import { statusHours } from "@/utils/deviceStatus";
defineProps({
  devices: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  error: { type: String, default: "" },
  hasLoaded: { type: Boolean, default: true }
});
const emit = defineEmits(["detail", "open"]);
const open = ref(false);
const container = ref(null);
const trigger = ref(null);
const popoverStyle = ref({});
function positionPopover() {
  if (!open.value || !trigger.value) return;
  const rect = trigger.value.getBoundingClientRect();
  const width = Math.min(400, window.innerWidth - 32);
  popoverStyle.value = {
    left: `${Math.max(
      16,
      Math.min(rect.right - width, window.innerWidth - width - 16)
    )}px`,
    top: `${rect.bottom + 12}px`
  };
}
function toggle() {
  open.value = !open.value;
  if (open.value) {
    positionPopover();
    nextTick(positionPopover);
  }
  emit("open");
}
function close(restoreFocus = false) {
  open.value = false;
  if (restoreFocus) trigger.value?.focus();
}
function selectDevice(device) {
  close();
  emit("detail", device);
}
function onPointerDown(event) {
  if (
    open.value &&
    !container.value?.contains(event.target) &&
    !document
      .getElementById("overuse-notification-panel")
      ?.contains(event.target)
  )
    close();
}
function onKeydown(event) {
  if (open.value && event.key === "Escape") {
    event.preventDefault();
    close(true);
  }
}
onMounted(() => {
  document.addEventListener("pointerdown", onPointerDown);
  document.addEventListener("keydown", onKeydown);
  window.addEventListener("resize", positionPopover);
  window.addEventListener("scroll", positionPopover, true);
});
onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", onPointerDown);
  document.removeEventListener("keydown", onKeydown);
  window.removeEventListener("resize", positionPopover);
  window.removeEventListener("scroll", positionPopover, true);
});
</script>

<style scoped>
.overuse-notifications {
  position: relative;
  flex-shrink: 0;
}
.overuse-bell {
  position: relative;
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  background: #fff;
  border: 1px solid #dededb;
  color: #171717;
  cursor: pointer;
}
.overuse-bell svg {
  width: 23px;
  height: 23px;
}
.overuse-bell:hover,
.overuse-bell[aria-expanded="true"] {
  background: #f5f5f3;
}
.overuse-bell:focus-visible,
.overuse-notification-item:focus-visible,
.overuse-close:focus-visible {
  outline: 2px solid #171717;
  outline-offset: 2px;
}
.overuse-badge {
  position: absolute;
  top: -7px;
  right: -7px;
  min-width: 20px;
  height: 20px;
  display: grid;
  place-items: center;
  padding: 0 5px;
  background: #171717;
  color: #fff;
  font-size: 10px;
  border: 2px solid #fff;
  border-radius: 20px;
}
.overuse-popover {
  position: fixed;
  top: calc(100% + 12px);
  z-index: 90;
  width: min(400px, calc(100vw - 32px));
  background: #fff;
  border: 1px solid #d4d4d0;
  box-shadow: 0 10px 32px #0000001a;
}
.overuse-popover header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 18px 8px;
}
.overuse-popover h3 {
  margin: 0 0 7px;
  font-size: 16px;
  color: #171717;
}
.overuse-popover header p {
  margin: 0;
  color: #737373;
  font-size: 12px;
  line-height: 1.5;
}
.overuse-close {
  border: 0;
  background: transparent;
  color: #666;
  font-size: 22px;
  cursor: pointer;
  padding: 0 3px;
}
.overuse-notification-count {
  padding: 0 18px 12px;
  margin: 0;
  font-size: 11px;
  color: #888;
}
.overuse-notification-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: min(380px, 50vh);
  overflow-y: auto;
  overscroll-behavior: contain;
  border-top: 1px solid #ededeb;
}
.overuse-notification-item {
  display: grid;
  gap: 5px;
  text-align: left;
  width: 100%;
  padding: 14px 18px;
  border: 0;
  border-bottom: 1px solid #ededeb;
  background: #fff;
  color: #171717;
  cursor: pointer;
  overflow-wrap: anywhere;
}
.overuse-notification-item:hover {
  background: #f5f5f3;
}
.overuse-notification-item strong {
  font-size: 13px;
}
.overuse-notification-item span {
  font-size: 12px;
  color: #737373;
}
.overuse-notification-item b {
  font-size: 12px;
  font-weight: 600;
}
.overuse-notification-empty {
  margin: 0;
  padding: 24px 18px;
  border-top: 1px solid #ededeb;
  font-size: 12px;
  color: #737373;
}
.overuse-refresh-error,
.overuse-loading {
  margin: 0;
  padding: 8px 18px;
  color: #8a5c16;
  font-size: 12px;
  line-height: 1.5;
}
</style>
